import { createContext, useState, useRef } from "react";
import { Action, ObjectType } from "../data/constants";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useUndoRedo } from "../hooks";
import { useDispatch, useSelector } from "react-redux";
import { getDrawDBApp } from "ee/selectors/drawdbSelectors";
import { editDrawDB } from "ee/actions/drawdbActions";
import { debounce } from "lodash";

export const EnumsContext = createContext(null);

export default function EnumsContextProvider({ children }) {
  const { t } = useTranslation();
  const [enums, setEnums] = useState([]);
  const { setUndoStack, setRedoStack } = useUndoRedo();

  const dispatch = useDispatch();
  const drawDBApp = useSelector(getDrawDBApp);

  const debouncedDispatch = useRef(
    debounce((action) => {
      dispatch(action);
    }, 300),
  ).current;

  const addEnum = (data, addToHistory = true) => {
    if (data) {
      setEnums((prev) => {
        const temp = prev.slice();
        temp.splice(data.id, 0, data);
        const enums = temp.map((t, i) => ({ ...t, id: i }));
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, enums },
          }),
        );
        return enums;
      });
    } else {
      setEnums((prev) => {
        const enums = [
          ...prev,
          {
            name: `enum_${prev.length}`,
            values: [],
          },
        ];
        debouncedDispatch(
          editDrawDB({
            id: drawDBApp.id,
            layout: { ...drawDBApp.layout, enums },
          }),
        );
        return enums;
      });
    }
    if (addToHistory) {
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.ADD,
          element: ObjectType.ENUM,
          message: t("add_enum"),
        },
      ]);
      setRedoStack([]);
    }
  };

  const deleteEnum = (id, addToHistory = true) => {
    if (addToHistory) {
      message.success(t("enum_deleted"));
      setUndoStack((prev) => [
        ...prev,
        {
          action: Action.DELETE,
          element: ObjectType.ENUM,
          id: id,
          data: enums[id],
          message: t("delete_enum", {
            enumName: enums[id].name,
          }),
        },
      ]);
      setRedoStack([]);
    }
    setEnums((prev) => {
      const enums = prev.filter((_, i) => i !== id);
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, enums },
        }),
      );
      return enums;
    });
  };

  const updateEnum = (id, values) => {
    setEnums((prev) => {
      const enums = prev.map((e, i) => (i === id ? { ...e, ...values } : e));
      debouncedDispatch(
        editDrawDB({
          id: drawDBApp.id,
          layout: { ...drawDBApp.layout, enums },
        }),
      );
      return enums;
    });
  };

  return (
    <EnumsContext.Provider
      value={{
        enums,
        setEnums,
        addEnum,
        updateEnum,
        deleteEnum,
        enumsCount: enums.length,
      }}
    >
      {children}
    </EnumsContext.Provider>
  );
}
